// Session persistence layer, extracted from GameBoard.jsx.
// Owns serializing the table (cards, piles, trackers, prompts, log)
// into a save blob, restoring it on resume (local saves, instance
// snapshots), the debounced replay keyframe, and the disk autosave.
// Match state itself is server-authoritative. Nothing here writes
// board/hand/pile truth to the network.
//
// Host contract: state, props (instanceId, isCasualSandbox), meshes,
// gameState rebuild helpers (_rebuildFromSnapshot et al. stay on the
// host), sync/connection, effectLedger, playerResources, log methods.

import { UI } from '../../../utils/arena/uiSounds';
import { createPileMesh, createTokenMesh } from '../../../utils/game/cardMesh';
import { createDiceMesh, disposeDiceMesh, placeDie } from '../../../utils/game/diceMesh';
import { EffectLedger } from '../../../utils/game/engine/automation';
import { mergeUnitHandViews, rehydrateCardSets, sanitizeUnitHandsForSeat, serializeCardSets } from '../../../utils/game/engine/core';
import { createGameState, createTrackerState } from '../../../utils/game/gameState';
import { isAuthMatchEnabled } from '../../../utils/game/net/authMatchFlag';
import { normalizeRollState, rehydrateGameLog, resolveRestoredMatchPhase, sanitizePromptStack } from '../../../utils/game/net/restoreHelpers';
import { listGameSessions, loadGameSession, loadSessionByInstance, saveGameSession } from '../../../utils/game/sessionStorage';
import { safeSetItem } from '../../../utils/localStorageQuota';
import { toast } from 'sonner';
import { t } from '../../../localization/t';

// Sanitize the local hand for over-the-wire transmission. The
// opponent only sees the card-back orientation (isSite) and any
// explicitly-revealed face. Magician's `_spellbookBack` propagates
// so the opponent's back-texture lookup matches; isSite is forced
// to false in that case so the opponent can't infer card type from
// any other receiver path either.
export function serializeHandForOpponent(host, hand) {
  return hand.map((c) => {
    const forceSpellbookBack = !!c._spellbookBack;
    const isSite = forceSpellbookBack ? false : (c.isSite || false);
    const base = forceSpellbookBack
      ? { isSite, _spellbookBack: true }
      : { isSite };
    return c.revealed
      ? { ...base, revealed: true, imageUrl: c.imageUrl, foiling: c.foiling }
      : base;
  });
}

// Set→array safe copy of a unit-hands map (spell instances may carry
// Set-typed bookkeeping fields, same as hand/pile cards).
function serializeUnitHands(unitHands) {
  const out = {};
  for (const [ownerId, entry] of Object.entries(unitHands || {})) {
    out[ownerId] = { controller: entry.controller, ownerArt: entry.ownerArt, spells: (entry.spells || []).map(serializeCardSets) };
  }
  return out;
}

// Public-snapshot form: every spell reduced to a face-down back (count +
// controller stay, identities do not) so the opponent's blob never
// carries faces. Each seat overlays its own controlled faces from the
// private channel on restore.
function publicUnitHands(unitHands) {
  const allBacks = sanitizeUnitHandsForSeat(unitHands, '__public__', (s) => ({ id: s.id, faceDown: true }));
  return allBacks;
}

// Per-seat private form: the seat's COMPLETE view. Real faces for the
// hands it controls, face-down backs for the rest. The server merges
// each seat's private over the public board with a generic spread
// (mergeSeatView), so this shape is what that seat ends up seeing. The
// same model as hands.
function seatViewUnitHands(unitHands, seat) {
  return sanitizeUnitHandsForSeat(unitHands, seat, (s) => ({ id: s.id, faceDown: true }));
}

function rehydrateUnitHands(unitHands) {
  const out = {};
  for (const [ownerId, entry] of Object.entries(unitHands || {})) {
    out[ownerId] = {
      controller: entry.controller,
      ownerArt: entry.ownerArt,
      spells: (entry.spells || []).map((s) => (s.faceDown && !s.cardId ? s : rehydrateCardSets(s))),
    };
  }
  return out;
}

export function serializeTableState(host) {
  // Set-typed bookkeeping fields (_abilitiesUsedThisTurn et al.) must
  // be converted to arrays before the blob hits JSON.stringify. A raw
  // Set silently flattens to `{}`, losing the once-per-turn/game flags
  // AND poisoning the restored card (Set method calls throw, bricking
  // the turn-phase chain). addCardToTable rehydrates on the way back.
  const tableCards = [];
  for (const [id, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    tableCards.push({
      ...serializeCardSets(card),
      x: mesh.position.x,
      y: mesh.position.y,
      z: mesh.position.z,
    });
  }
  return {
    name: 'Autosave',
    tableCards,
    // Hand and pile cards get the same Set→array treatment. Once-per-
    // game flags survive zone changes (resetCardOnLeavePlay keeps
    // _abilitiesUsedThisGame), so a card in the cemetery or hand can
    // still carry Sets. They re-enter play through addCardToTable,
    // which rehydrates.
    piles: (host.state.gameState.piles || []).map((p) => ({
      ...p,
      cards: (p.cards || []).map(serializeCardSets),
    })),
    handCards: (host.state.handCards || []).map(serializeCardSets),
    // Owner-locked unit hands. The FULL map (with faces) rides the
    // local / per-seat private save; the public snapshot strips faces
    // (see emitAuthState / scheduleLocalSave / pushStateSyncToPeer).
    unitHands: serializeUnitHands(host.state.gameState.unitHands),
    // Casual-sandbox-only fields. P2 hand + active player + per-
    // player mulligan state. Multiplayer saves omit these and
    // restoreSession falls back to legacy single-hand behaviour.
    handCards2: (host.state.handCards2 || []).map(serializeCardSets),
    activePlayerNum: host.state.activePlayerNum,
    mulliganPhase: host.state.mulliganPhase,
    mulliganPhase2: host.state.mulliganPhase2,
    mulliganFlaggedIds: Array.from(host.state.mulliganFlaggedIds || []),
    mulliganFlaggedIds2: Array.from(host.state.mulliganFlaggedIds2 || []),
    // v1.105. Disconnect-survival index. JSON-safe descriptors for
    // every critical prompt the player has open. Rehydrated by
    // restoreSession → _rehydratePromptStack so a tab close mid-
    // prompt resumes exactly where the player left off.
    pendingPromptStack: host.state.pendingPromptStack || [],
    // Death's Door entry stamps (turn each player reached 0 life in).
    // A reload mid-turn must not forget that a player entered DD this
    // very turn. Losing the stamp would allow a premature death blow.
    deathsDoorStamps: host.playerResources?.serializeDeathsDoor?.() || null,
    // Match lifecycle. What phase the game is in and whose turn
    // it is. Without these, a reload drops the user back to
    // "before Start Game" with turn 1 for P1 even if they'd
    // already started and passed a few turns.
    gameStarted: host.state.gameStarted,
    currentTurn: host.state.currentTurn,
    turnNumbers: host.state.turnNumbers,
    // First-player roll lifecycle. matchPhase === 'rollOff' means
    // the dice modal was active when the snapshot was taken. On
    // restore we re-open it seeded with the persisted rolls so a
    // mid-roll close lands the user back in the same place
    // instead of in a corrupt pre-game state.
    matchPhase: host.state.matchPhase || 'playing',
    firstPlayerRollState: host.state.firstPlayerRollState || null,
    // Avatar pointer. The on-table avatars are already serialised
    // via tableCards, but several helpers (rule resolution, mana
    // recalc) read directly from gameState.avatarCard. Preserve it
    // so they find the avatar without falling back to a mesh scan.
    avatarCard: serializeCardSets(host.state.gameState?.avatarCard) || null,
    // Original deck object per slot. Powers End Game's fresh-board
    // reset (re-runs spawnSelectedDeck with these). Without them
    // an End Game after a reload would wipe the board with nothing
    // to re-spawn, leaving the slots empty.
    spawnedDecks: host.state.spawnedDecks || { 1: null, 2: null },
    // No 'casual' marker any more: solo boards are server-durable real
    // matches (resume pulls the mirror, never a local casual blob). Legacy
    // saves that carry mode:'casual' are still readable until D3.
    spawnConfig: host.state.spawnConfig,
    tokens: host.state.gameState.tokens.map((t) => {
      const mesh = host.tokenMeshes.get(t.id);
      return { ...t, x: mesh?.position.x ?? t.x, z: mesh?.position.z ?? t.z };
    }),
    trackers: host.state.gameState.trackers,
    // Continuous-effect ledger. Automation auras (Onyx Core +1
    // mana, Black Obelisk threshold grants) AND manual HUD buffs
    // live here. Without this, every save/load wipes the active
    // buffs for both seats and the mana / threshold display
    // disagrees with the cards in play.
    effectLedger: host.effectLedger.serialize(),
    dice: (host.state.gameState.dice || []).map((d) => ({ ...d })),
    // Full action log. Time is serialized as a number so JSON
    // round-trips cleanly; restoreSession rehydrates it back to
    // a Date. The log is per-player-perspective ('you' vs
    // 'opponent') and lives only in the author's save, so each
    // side restores their own view intact.
    gameLog: (host.state.gameLog || []).map((e) => ({
      ...e,
      time: e.time instanceof Date ? e.time.getTime() : e.time,
    })),
  };
}

export function restoreSession(host, session) {
  host.sync.setSuppressed(true);
  // Clear existing table
  for (const [, mesh] of host.meshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
  }
  host.meshes.clear();
  for (const [, mesh] of host.pileMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
  }
  host.pileMeshes.clear();

  // Restore the effect ledger BEFORE rebuilding meshes. ApplyCard
  // re-runs automation as cards re-enter, and the entries.some(...)
  // guard at the GRANT_MANA install site relies on the ledger being
  // pre-populated so re-entering cards don't double-install their
  // auras on top of the persisted entry.
  host.effectLedger = EffectLedger.deserialize(session.effectLedger || { entries: [] });

  // Restore piles
  const gameState = createGameState(host.props.startingHp || 20);
  gameState.piles = session.piles || [];
  // Owner-locked unit hands. A live state:sync / public snapshot adopt
  // carries all-backs; keep the faces this seat already holds locally so
  // adopting a peer's board doesn't blank the controller's own hand.
  // Under auth the disk blob is a legacy backstop, NOT authoritative: sanitize
  // it to THIS seat's entitlement before adopting so a foreign/last-writer blob
  // (e.g. two accounts sharing one device's session store) can never inject
  // faces this seat isn't entitled to. The server mirror re-supplies the seat's
  // own controlled faces via match:full, so nothing legitimate is lost.
  const restoredUnitHands = host._authZonesEnabled?.()
    ? seatViewUnitHands(rehydrateUnitHands(session.unitHands), host._localSeat())
    : rehydrateUnitHands(session.unitHands);
  gameState.unitHands = mergeUnitHandViews(
    restoredUnitHands,
    host.state.gameState?.unitHands || {},
  );
  // Persisted trackers preserve the HP the match was started with.
  // including mid-match life changes. So a Quick Match resumed at
  // 7 HP stays at 7 HP. Fresh trackers fall back to the prop.
  gameState.trackers = session.trackers || createTrackerState(host.props.startingHp || 20);
  // Re-hydrate the avatar pointer so helpers that read
  // gameState.avatarCard (affinity recalc, rule resolution) find
  // the avatar without needing to scan meshes. The corresponding
  // avatar card itself is restored via tableCards below.
  // The pointer is a standalone copy (not the tableCards instance), so
  // it needs its own Set rehydration. AddCardToTable never sees it.
  if (session.avatarCard) gameState.avatarCard = rehydrateCardSets(session.avatarCard);
  // Carry the in-flight combat record (injected from the durable match:combat
  // field on reconnect) into the restored state; _rehydrateCombat (after
  // meshes rebuild) re-opens the prompt.
  // The in-flight combat record lives in its OWN durable channel
  // (match:combat) and is NOT part of the board blob. On reconnect TWO
  // restores race: the auth snapshot adopt (which injects the server's
  // combat record under the `pendingCombat` key. Possibly an explicit
  // null) and the local-blob / P2P restore (whose session has NO such
  // key). A source that doesn't speak combat must not clobber it.
  // otherwise the second restore wipes the fight the first one just
  // resumed and the assign/defend prompt vanishes mid-reconnect.
  if ('pendingCombat' in session) {
    gameState.pendingCombat = session.pendingCombat || null;
  } else {
    gameState.pendingCombat = host.state.gameState?.pendingCombat || null;
  }
  if (session.deathsDoorStamps) host.playerResources?.restoreDeathsDoor?.(session.deathsDoorStamps);
  // Hydrate casual-sandbox-only fields when present. Saves that
  // predate dual-hand mode fall back to the legacy single-hand
  // defaults. Which is exactly what multiplayer sessions expect.
  //
  // Defensive: a populated firstPlayerSeat proves the roll resolved,
  // so matchPhase MUST be 'playing' regardless of what the blob's
  // matchPhase field says. Defends against (a) autoSave write races
  // where a stale 'rollOff' POST lands on disk after a 'playing' one
  // (autoSave is not coalesced. Multiple force-saves can be in
  // flight during the dice flow), and (b) any code path that fails
  // to flip matchPhase to 'playing' after the roll resolved (e.g.
  // Harbinger setup sequence onComplete not firing for the guest).
  // Without this, the guest's resume re-opens the dice modal in
  // IDLE phase even though the roll already happened.
  const restoredMatchPhase = resolveRestoredMatchPhase(session);
  const restoredRollState = normalizeRollState(session.firstPlayerRollState);
  const restoration = {
    gameState,
    handCards: session.handCards || [],
    handCards2: session.handCards2 || [],
    activePlayerNum: session.activePlayerNum || 1,
    mulliganPhase: session.mulliganPhase || null,
    mulliganPhase2: session.mulliganPhase2 || null,
    mulliganFlaggedIds: new Set(session.mulliganFlaggedIds || []),
    mulliganFlaggedIds2: new Set(session.mulliganFlaggedIds2 || []),
    // v1.105. Restore the prompt stack so _rehydratePromptStack
    // (fired right after this setState lands) sees the saved
    // descriptors. Sanitised to plain-array of plain-object shape.
    pendingPromptStack: sanitizePromptStack(session.pendingPromptStack),
    // Match lifecycle: keep the user exactly where they left off.
    // if the game was in progress, the Start button stays hidden
    // and turn display continues from the saved turn.
    gameStarted: !!session.gameStarted,
    currentTurn: session.currentTurn || 'p1',
    turnNumbers: session.turnNumbers || { p1: 1, p2: 0 },
    spawnedDecks: session.spawnedDecks || { 1: null, 2: null },
    // First-player roll lifecycle. If the snapshot was taken mid-
    // roll, re-open the dice modal seeded with the saved rolls so
    // the user lands back in the same place. The modal initialises
    // from initialRollState and skips the ENTRY animation when any
    // roll is already locked in. Legacy saves without these fields
    // resume to 'playing' (their default) with no modal.
    matchPhase: restoredMatchPhase,
    firstPlayerRollState: restoredRollState,
    showFirstPlayerDice: restoredMatchPhase === 'rollOff',
    gameLog: rehydrateGameLog(session.gameLog),
  };
  host.setState(restoration);

  // Notify MatchAssetGate of the opponent's deck so phase 2 runs on
  // reconnect/resume. Fresh matches go through _queueOpponentSpawn,
  // which fires this callback as part of the spawn flow; resume paths
  // (host-rejoin, guest-rejoin, session:active auto-rejoin) bypass
  // that and call restoreSession directly, leaving the gate's
  // opponentDeck prop null forever and the loading overlay stuck.
  // Empty-stub fallback lets the gate complete on legacy snapshots
  // or very-early reconnects where spawnedDecks isn't populated yet.
  const opponentSlot = host.state.isHost ? 2 : 1;
  const restoredOpponentDeck = restoration.spawnedDecks?.[opponentSlot] || { cards: [] };
  host.props.onOpponentDeckResolved?.(restoredOpponentDeck);
  if (restoredMatchPhase === 'rollOff') {
    // Hold the tutorial behind the resumed dice modal so the
    // spotlight doesn't measure targets that are about to be hidden.
    // _resolveFirstPlayerRoll releases this on the next pick.
    host._diceModalPending = true;
  }

  for (const pile of gameState.piles) {
    const mesh = createPileMesh(pile);
    if (mesh) {
      host.scene.scene.add(mesh);
      host.pileMeshes.set(pile.id, mesh);
    }
  }

  // Restore table cards. broadcast=false silences both the
  // sync emit (suppressed anyway under setSuppressed but kept
  // explicit) AND the 'Played X' log entry that addCardToTable
  // fires on the broadcast path. Without the explicit false, a
  // restoreSession would dump one "Played X" line per card into
  // the log for every peer, making both sides look like they
  // just played every card on the board.
  for (const card of session.tableCards || []) {
    host.addCardToTable(card, false);
  }

  // Restore tokens
  for (const [, mesh] of host.tokenMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry?.dispose();
    mesh.material?.dispose();
  }
  host.tokenMeshes.clear();

  gameState.tokens = session.tokens || [];
  for (const token of gameState.tokens) {
    const mesh = createTokenMesh(token);
    host.scene.scene.add(mesh);
    host.tokenMeshes.set(token.id, mesh);
  }

  // Restore dice
  for (const [, mesh] of host.diceMeshes) disposeDiceMesh(mesh);
  host.diceMeshes.clear();

  gameState.dice = session.dice || [];
  for (const dice of gameState.dice) {
    const mesh = createDiceMesh(dice);
    host.scene.scene.add(mesh);
    host.diceMeshes.set(dice.id, mesh);
    // Cards are already back on the table, so each die finds its card.
    placeDie(mesh, host._diceContext());
  }

  if (session.spawnConfig) {
    host.setState({ spawnConfig: session.spawnConfig }, () => {
      host.createTrackerTokens();
      host.createTrackerButtons();
    });
  } else {
    host.createTrackerTokens();
    host.createTrackerButtons();
  }
  host.sync.setSuppressed(false);
  // v1.105. Re-fire openers for every saved critical prompt. Deferred
  // to the next tick so meshes added above have actually landed before
  // any rehydrator tries to look them up. Restored descriptors live on
  // state.pendingPromptStack from the setState above.
  setTimeout(() => {
    if (host._unmounted) return;
    host._rehydratePromptStack(host.state.pendingPromptStack);
    host._rehydrateCombat();
  }, 0);
}

// Backstop for the first-player-dice gate: if the authoritative Full never
// lands (server slow, socket flapping), release the gate after this long so a
// genuinely fresh match still rolls.
const AUTH_FULL_GATE_MS = 6000;

// Gate the first-player dice until the authoritative Full resolves: a
// reconnect / app-restart must fold the server board before the mount timer
// may pop the dice, else it clobbers matchPhase to 'rollOff' over the real
// game (the reload board-wipe bug). The Full fold clears the flag; the
// timeout is the backstop. Armed on every authoritative pull.
export function armAuthStateGate(host) {
  host._authSnapshotPending = true;
  clearTimeout(host._authSnapshotGateTimer);
  host._authSnapshotGateTimer = setTimeout(() => {
    host._authSnapshotPending = false;
    host._maybeArmFirstPlayerDice?.();
  }, AUTH_FULL_GATE_MS);
}

export function clearLocalGameState(host) {
  const roomCode = host.state.roomCode;
  if (!roomCode) return;
  try { localStorage.removeItem(`valkenhall-game-state-${roomCode}`); } catch {}
}

// Invalidate the 10s keyframe gate so the very next scheduleLocalSave
// records a fresh replay keyframe immediately. Call this around any
// matchPhase transition (rollOff → playing, playing → finished, etc.).
export function flushSharedStateOnNextSave(host) {
  host._lastSharedSaveAt = 0;
  host.scheduleLocalSave();
}

// Debounced replay keyframe. Match state is server-authoritative; the only
// thing persisted here is the host's public keyframe for the match recorder
// (replay/analytics), 10s-gated to avoid excessive volume.
export function scheduleLocalSave(host) {
  if (!host.props.isArenaMatch) return;
  const roomCode = host.state.roomCode;
  if (!roomCode) return;
  clearTimeout(host._localSaveTimer);
  host._localSaveTimer = setTimeout(() => {
    if (!host.state.isHost) return;
    const now = Date.now();
    if (host._lastSharedSaveAt && now - host._lastSharedSaveAt <= 10000) return;
    host._lastSharedSaveAt = now;
    try {
      const state = host.serializeTableState();
      state.roomCode = roomCode;
      const pub = { ...state, handCards: [], unitHands: publicUnitHands(state.unitHands), opponentHandInfo: host._serializeHandForOpponent(host.state.handCards) };
      host.matchRecorder.recordKeyframe(pub);
    } catch {}
  }, 500);
}

// Attaches the instance id + visibility/mode to a serialized
// state blob so the unified instance/save system can look it up
// later via loadSessionByInstance(). No-op when the board was
// launched outside an instance (legacy solo paths pre-migration).
export function tagWithInstance(host, state) {
  const id = host.props.instanceId;
  if (!id) return state;
  state.instanceId = id;
  // Stamp the author so loadSessionByInstance can filter by
  // user when two Electron processes share SESSIONS_DIR (dev
  // dual-login). Without it, the guest's blob can stomp the
  // host's and vice versa.
  if (host.props.profile?.id) state.userId = host.props.profile.id;
  // Prefer the canonical instance metadata threaded via props.
  // it's authoritative. Fall back to the legacy casualSandbox
  // gate for pre-instance saves, then to the blob's own tags.
  state.visibility = host.props.instanceVisibility
    || state.visibility
    || (host.props.isCasualSandbox ? 'solo' : 'solo');
  state.mode = host.props.instanceMode
    || state.mode
    || (host.props.isCasualSandbox ? 'sandbox' : 'match');
  return state;
}

// Every save for a given instance targets a single per-user file.
// `save-${userId}-${instanceId}`. autoSave, quickSave, manualSave,
// and force-save-on-exit all write the SAME blob, so the user's
// chosen name survives across overwrites (instead of getting
// clobbered by an "Autosave" name on the next exit). Legacy solo
// saves without an instanceId still use the generic 'autosave' id.
export function instanceSaveId(host) {
  const profileId = host.props.profile?.id;
  // Draft round matches take precedence: each Swiss round has
  // its own matchId and the save is scoped to THAT. Otherwise
  // every round overwrites the previous round's blob and
  // mid-match resume would restore state from a different pairing.
  if (host.props.draftMatchId && profileId) {
    return `save-${profileId}-draft-${host.props.draftMatchId}`;
  }
  if (host.props.instanceId && profileId) {
    return `save-${profileId}-${host.props.instanceId}`;
  }
  return null;
}

// Coalesce concurrent saves: at most one POST in flight, with at
// most one follow-up queued behind it. Prevents an older save
// (e.g. matchPhase='rollOff' captured at modal-open) from landing
// on disk after a newer one (matchPhase='playing' captured at the
// mulligan-setup transition) when the dice flow fires force-saves
// in quick succession. Without serialising these the on-disk
// content depends on HTTP completion order rather than state-update
// order. Exactly the corruption that re-opens the dice modal on
// a guest's resume. The trailing-follow-up rule guarantees the
// disk catches up to the latest in-memory state once a busy burst
// settles, since the LAST queued call re-serialises live state at
// the moment it actually runs.
export function autoSave(host, { force = false } = {}) {
  if (host._autoSaveInFlight) {
    host._autoSavePending = host._autoSavePending || { force };
    if (force) host._autoSavePending.force = true;
    return host._autoSaveInFlight;
  }
  const promise = host._doAutoSave({ force }).finally(() => {
    host._autoSaveInFlight = null;
    const pending = host._autoSavePending;
    if (pending) {
      host._autoSavePending = null;
      host.autoSave(pending).catch(() => {});
    }
  });
  host._autoSaveInFlight = promise;
  return promise;
}

export async function doAutoSave(host, { force = false } = {}) {
  // Host AND guest persist their own local blob. Each blob
  // carries only the author's perspective: host has the
  // authoritative board, guest has their own hand and mulligan
  // state. On resume each player loads their own blob so
  // nothing is re-drawn from scratch. Legacy non-instance solo
  // saves stay host-only.
  // Anyone with an instance-scoped save id (normal instance or
  // draft round match) may autosave. Legacy non-scoped solo
  // saves stay host-only.
  const hasSaveScope = !!host._instanceSaveId();
  if (!host.state.isHost && !hasSaveScope) return;
  const state = host.serializeTableState();
  const hasTrackers = state.trackers && Object.values(state.trackers).some((p) =>
    Object.values(p).some((v) => v > 0)
  );
  const hasContent = state.tableCards.length > 0 || state.handCards.length > 0 || state.piles.some((p) => p.cards.length > 0) || (state.tokens && state.tokens.length > 0) || (state.dice && state.dice.length > 0) || hasTrackers;
  // Skip empty-board saves for the periodic timer (there's
  // nothing interesting to capture yet), BUT force-write for
  // explicit caller-triggered saves. Save & Leave MUST
  // persist the blob or the instance-level Resume path has
  // nothing to load. An instance/draft-match-tagged save with
  // an empty board is a valid "I was here and left" marker.
  if (!hasContent && !force && !hasSaveScope) return;

  state.id = host._instanceSaveId() || 'autosave';
  // Preserve the user's chosen name if they've used Save As...
  // Only falls back to "Autosave" if the file has never been
  // named. This keeps "My Friday Game" as the label across
  // every subsequent auto-save + force-save-on-exit.
  state.name = host.state.currentSessionName || 'Autosave';
  host._tagWithInstance(state);
  try {
    const summary = await saveGameSession(state);
    if (!host.state.currentSessionId && summary?.id) {
      host.setState({ currentSessionId: summary.id });
    }
  } catch (error) {
    console.warn('Auto-save failed:', error);
  }
}

export async function manualSave(host, name, existingId = null) {
  if (!host.state.isHost) return;
  const state = host.serializeTableState();
  state.name = name || `Game ${new Date().toLocaleString()}`;
  // Per-instance save id wins when we have one, so Save As...
  // writes to the same file autoSave uses. existingId is a
  // pre-instance escape hatch still honored by legacy solo
  // saves that don't have a bound instance.
  state.id = host._instanceSaveId() || existingId || undefined;
  host._tagWithInstance(state);
  try {
    const summary = await saveGameSession(state);
    host.setState({
      showSaveDialog: false,
      currentSessionId: summary.id,
      currentSessionName: state.name,
    });
    toast.success(t('Saved "{name}"', { name: state.name }));
  } catch (error) {
    console.error('Failed to save session:', error);
    toast.error(t('Failed to save session'));

  }
}

export async function quickSave(host) {
  if (!host.state.isHost) return;
  const { currentSessionId, currentSessionName } = host.state;
  if (currentSessionId && currentSessionId !== 'autosave') {
    await host.manualSave(currentSessionName, currentSessionId);
  } else {
    host.openSaveDialog();
  }
}

export async function openSaveDialog(host) {
  try {
    const sessions = await listGameSessions();
    host.setState({ showSaveDialog: true, savedSessions: sessions, saveDialogName: host.state.currentSessionName || '' });
  } catch {
    host.setState({ showSaveDialog: true, savedSessions: [], saveDialogName: '' });
  }
}
